import { type MouseEvent, useState } from 'react'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  type TableOptions,
  useReactTable,
} from '@tanstack/react-table'

import styles from './DataTable.module.scss'

interface DataTableProps<T> {
  data: T[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<T, any>[]
  isLoading?: boolean
  emptyMessage?: string
  loadingMessage?: string
  ariaLabel?: string
  onRowClick?: (row: T, event: MouseEvent<HTMLTableRowElement>) => void
  // Server-driven sort: when `manualSorting` is true the consumer owns the
  // sorting state and the backend has already ordered `data`. Pair with
  // controlled `sorting` + `onSortingChange`.
  manualSorting?: boolean
  // Server-driven pagination: when true, `data` is the current page slice and
  // react-table won't try to paginate it client-side.
  manualPagination?: boolean
  sorting?: SortingState
  onSortingChange?: OnChangeFn<SortingState>
  // Row selection: consumers add their own checkbox column (so they control
  // styling and "select-all on page" header semantics). The DataTable just
  // exposes the plumbing.
  enableRowSelection?: boolean
  rowSelection?: RowSelectionState
  onRowSelectionChange?: OnChangeFn<RowSelectionState>
  getRowId?: (row: T, index: number) => string
}

function DataTable<T>({
  data,
  columns,
  isLoading = false,
  emptyMessage = 'No data.',
  loadingMessage = 'Loading...',
  ariaLabel,
  onRowClick,
  manualSorting,
  manualPagination,
  sorting,
  onSortingChange,
  enableRowSelection,
  rowSelection,
  onRowSelectionChange,
  getRowId,
}: DataTableProps<T>) {
  const [localSorting, setLocalSorting] = useState<SortingState>([])

  const isSortControlled = sorting !== undefined && onSortingChange !== undefined
  const effectiveSorting = isSortControlled ? sorting : localSorting
  const effectiveOnSortingChange: OnChangeFn<SortingState> = isSortControlled ? onSortingChange : setLocalSorting

  const tableOptions: TableOptions<T> = {
    data,
    columns,
    state: {
      sorting: effectiveSorting,
      ...(enableRowSelection ? { rowSelection: rowSelection ?? {} } : {}),
    },
    onSortingChange: effectiveOnSortingChange,
    getCoreRowModel: getCoreRowModel(),
    // react-table requires `getSortedRowModel` to be omitted when sorting is
    // server-driven, otherwise it would re-sort the already-ordered page.
    ...(manualSorting ? { manualSorting: true } : { getSortedRowModel: getSortedRowModel() }),
    ...(manualPagination ? { manualPagination: true } : {}),
    ...(enableRowSelection
      ? {
          enableRowSelection: true,
          onRowSelectionChange,
          ...(getRowId ? { getRowId } : {}),
        }
      : {}),
  }

  const table = useReactTable(tableOptions)

  if (isLoading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.loadingState}>{loadingMessage}</div>
      </div>
    )
  }

  if (data.length === 0) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.emptyState}>{emptyMessage}</div>
      </div>
    )
  }

  return (
    <div className={styles.tableContainer}>
      <table className={styles.table} aria-label={ariaLabel}>
        <thead className={styles.tableHeader}>
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <th
                  key={header.id}
                  scope="col"
                  className={header.column.getCanSort() ? styles.sortableHeader : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  {{ asc: ' ↑', desc: ' ↓' }[header.column.getIsSorted() as string] ?? ''}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody className={styles.tableBody}>
          {table.getRowModel().rows.map((row) => (
            <tr
              key={row.id}
              className={onRowClick ? styles.clickableRow : undefined}
              onClick={onRowClick ? (event) => onRowClick(row.original, event) : undefined}
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default DataTable
